import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/testIds";
import { toast } from "sonner";
import {
  ArrowLeft,
  MessageCircle,
  FileText,
  RefreshCw,
  Pencil,
  Trash2,
  Phone,
  Award,
  Wallet,
  CalendarDays,
  StickyNote,
} from "lucide-react";
import {
  buildReminderMessage,
  buildReceiptMessage,
  openWhatsApp,
  QUICK_TEMPLATES,
} from "@/lib/whatsapp";


// Itemised transactions plus any earlier money held only in `amount_received`.
function paymentsOf(s) {
  if (Array.isArray(s?.payments) && s.payments.length > 0) return s.payments;
  const txs = Array.isArray(s?.transactions) ? s.transactions : [];
  const tracked = txs.reduce((n, t) => n + Number(t?.amount || 0), 0);
  const rem = Number(s?.amount_received || 0) - tracked;
  const rows = txs.map((t) => ({ amount: Number(t?.amount || 0), date: String(t?.date || "") }));
  if (rem > 0.005) {
    rows.push({
      amount: rem,
      date: String(
        s?.legacy_payment_date ||
          (txs.length > 0 ? s?.start_date : s?.received_date || s?.start_date) ||
          ""
      ),
      legacy: true,
    });
  }
  return rows.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
}

export default function StudentDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewDate, setRenewDate] = useState("");
  const [renewAmount, setRenewAmount] = useState("");
  const [msgOpen, setMsgOpen] = useState(false);
  const [customMsg, setCustomMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setS(await api.getStudent(id));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !s) {
    return <div className="text-center py-16 text-slate-500">Loading...</div>;
  }

  const statusColor = s.status === "active" ? "#34C759" : s.status === "expiring" ? "#FF9500" : "#FF3B30";

  const doRenew = async () => {
    if (!renewDate) return toast.error("Pick a new end date");
    const amt = Number(renewAmount);
    if (!amt || amt <= 0) return toast.error("Enter the amount received");
    try {
      const updated = await api.renewStudent(id, renewDate, amt);
      setS(updated);
      setRenewOpen(false);
      setRenewDate("");
      setRenewAmount("");
      toast.success("Module renewed");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete ${s.name}? This cannot be undone.`)) return;
    try {
      await api.deleteStudent(id);
      toast.success("Student deleted");
      nav("/students");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Link to="/students" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" />
        Back to students
      </Link>

      {/* Hero */}
      <div
        className="rounded-2xl p-6 text-white relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0A2540 0%, #143A5B 100%)" }}
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center font-extrabold text-2xl">
            {s.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-extrabold truncate">{s.name}</h1>
            <p className="text-white/80 text-sm mt-0.5">
              Age {s.age} · {s.phone_number}
            </p>
            <span
              className="inline-flex mt-2 px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider"
              style={{ background: statusColor, color: "#fff" }}
            >
              {s.status}
              {s.days_left >= 0 ? ` · ${s.days_left}d left` : ` · ${Math.abs(s.days_left)}d ago`}
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <ActionButton
          onClick={() => openWhatsApp(s.phone_number, buildReminderMessage(s))}
          icon={MessageCircle}
          label="Renewal Reminder"
          color="#25D366"
          testId={`whatsapp-renewal-${s.id}`}
        />
        {isAdmin && (
          <ActionButton
            onClick={() => openWhatsApp(s.phone_number, buildReceiptMessage(s))}
            icon={FileText}
            label="E-Receipt"
            color="#25D366"
            testId={TID.studentSendReceipt}
          />
        )}
        <ActionButton
          onClick={() => setMsgOpen(true)}
          icon={MessageCircle}
          label="Send Message"
          color="#FF6B6B"
          testId={TID.studentSendMsg}
        />
        {isAdmin && (
          <ActionButton
            onClick={() => setRenewOpen(true)}
            icon={RefreshCw}
            label="Renew"
            color="#0A2540"
            testId={TID.studentRenewBtn}
          />
        )}
      </div>

      {/* Details */}
      <div className="bg-slate-50 rounded-xl border border-slate-100 p-6 space-y-3">
        <Row icon={CalendarDays} label="Module" value={`${s.start_date} → ${s.end_date}`} />
        <Row icon={Wallet} label="Amount Received" value={`₹${Number(s.amount_received || 0).toLocaleString("en-IN")}`} />
        <Row
          icon={Wallet}
          label="Pending Fee"
          value={
            s.pending_fee > 0
              ? `₹${Number(s.pending_fee).toLocaleString("en-IN")}${s.next_due_date ? ` · due ${s.next_due_date}` : ""}`
              : "No dues"
          }
        />
        {s.coach_name && <Row icon={Award} label="Coach" value={s.coach_name} />}
        <Row icon={Phone} label="Phone" value={s.phone_number} />
        {s.comments && (
          <Row
            icon={StickyNote}
            label="Comments"
            value={<span data-testid="student-detail-comments" className="whitespace-pre-wrap">{s.comments}</span>}
          />
        )}
      </div>

      {/* Attendance this month */}
      <AttendanceThisMonth studentId={s.id} />

      {/* Payment History */}
      {paymentsOf(s).length > 0 && (
        <div className="bg-slate-50 rounded-xl border border-slate-100 p-6 space-y-3">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Payment History
          </p>
          <ul data-testid="student-transactions" className="divide-y divide-slate-200">
            {paymentsOf(s).slice().reverse().map((t, i) => (
              <li key={i} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-700 font-medium">
                  {t.date}
                  {t.legacy && (
                    <span className="ml-2 text-xs font-semibold text-slate-500">
                      Opening balance
                    </span>
                  )}
                </span>
                <span className="text-slate-900 font-semibold">
                  ₹{Number(t.amount || 0).toLocaleString("en-IN")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Admin actions */}
      {isAdmin && (
        <div className="flex items-center gap-3">
          <Link
            to={`/students/${s.id}/edit`}
            data-testid={TID.studentEditLink}
            className="inline-flex items-center gap-2 h-10 px-4 border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Pencil className="w-4 h-4" />
            Edit
          </Link>
          <button
            data-testid={TID.studentDeleteBtn}
            onClick={doDelete}
            className="inline-flex items-center gap-2 h-10 px-4 border border-red-200 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}

      {/* Renew Dialog */}
      {renewOpen && (
        <Modal onClose={() => setRenewOpen(false)} title="Renew Module">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">
                New End Date
              </label>
              <input
                data-testid={TID.studentRenewDate}
                type="date"
                value={renewDate}
                onChange={(e) => setRenewDate(e.target.value)}
                className="w-full h-11 px-3 border border-slate-200 rounded-lg bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">
                Amount Received
              </label>
              <input
                data-testid={TID.studentRenewAmount}
                type="number"
                min={0}
                step="0.01"
                value={renewAmount}
                onChange={(e) => setRenewAmount(e.target.value)}
                placeholder="Amount paid"
                className="w-full h-11 px-3 border border-slate-200 rounded-lg bg-white"
              />
            </div>
            <button
              onClick={doRenew}
              data-testid={TID.studentRenewSubmit}
              className="w-full h-11 bg-[#0A2540] hover:bg-[#081D33] text-white rounded-lg font-semibold"
            >
              Confirm Renewal
            </button>
          </div>
        </Modal>
      )}

      {/* Send Message Dialog */}
      {msgOpen && (
        <Modal onClose={() => setMsgOpen(false)} title="Send WhatsApp Message">
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Quick templates</p>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_TEMPLATES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => {
                    openWhatsApp(s.phone_number, t.body(s));
                    setMsgOpen(false);
                  }}
                  className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 text-left"
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider mt-3">
                Custom Message
              </label>
              <textarea
                rows={4}
                value={customMsg}
                onChange={(e) => setCustomMsg(e.target.value)}
                className="w-full p-3 border border-slate-200 rounded-lg bg-white text-sm"
                placeholder="Type a custom WhatsApp message..."
              />
              <button
                disabled={!customMsg.trim()}
                onClick={() => {
                  openWhatsApp(s.phone_number, customMsg.trim());
                  setMsgOpen(false);
                  setCustomMsg("");
                }}
                className="mt-2 w-full h-10 bg-[#25D366] hover:bg-[#1DA851] disabled:opacity-50 text-white rounded-lg font-semibold text-sm"
              >
                Send via WhatsApp
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ActionButton({ icon: Icon, label, color, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="flex flex-col items-center gap-1 p-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
    >
      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: color + "22" }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <span className="text-xs font-semibold text-slate-700 text-center">{label}</span>
    </button>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-slate-400 mt-1 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</p>
        <p className="text-sm text-slate-900 font-medium mt-0.5 break-words">{value}</p>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-bold text-lg text-slate-900 mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}


function AttendanceThisMonth({ studentId }) {
  const [state, setState] = useState({ loading: true, dates: [], month: "" });
  useEffect(() => {
    let alive = true;
    api
      .studentAttendanceDates(studentId)
      .then((r) => alive && setState({ loading: false, dates: r.dates, month: r.month }))
      .catch(() => alive && setState({ loading: false, dates: [], month: "" }));
    return () => {
      alive = false;
    };
  }, [studentId]);
  const monthLabel = state.month
    ? new Date(state.month + "-01").toLocaleString("en-IN", { month: "long", year: "numeric" })
    : "";
  return (
    <div className="bg-slate-50 rounded-xl border border-slate-100 p-6 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
          Attendance · {monthLabel}
        </p>
        <span className="text-xs font-semibold text-slate-500">
          {state.loading ? "..." : `${state.dates.length} day${state.dates.length === 1 ? "" : "s"}`}
        </span>
      </div>
      {state.loading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : state.dates.length === 0 ? (
        <p className="text-sm text-slate-500">Not marked present this month yet.</p>
      ) : (
        <ul
          data-testid="student-attendance-dates"
          className="flex flex-wrap gap-1.5"
        >
          {state.dates.map((d) => (
            <li
              key={d}
              className="px-2 py-1 text-xs font-semibold text-[#0A8F3D] bg-[#34C75922] rounded-md"
            >
              {d}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
